import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import QRCode from 'qrcode';
import { useParams, useLocation } from 'react-router-dom';
import { reportingApi, devFilesApi, fetchImageAsDataUrl, reportPdfJobsApi } from '../../services/api';
import { activeDbName, activeDbSlug } from '../../services/activeDatabase';
import { useNotifications } from '../../context/NotificationContext';
import { useDataTransfer } from '../../context/DataTransferContext';
import ReportParameterTable from './components/ReportParameterTable';
import ReportPaginator from './components/ReportPaginator';
import ReportSubscriptionModal from './ReportSubscriptionModal';
import { useAuth } from '../../context/AuthContext';
import { FileSpreadsheet, GripVertical, Ban, ChevronDown, ChevronRight, Play, FilterX, Download, Mail } from 'lucide-react';
import wmLogo from '../../assets/logos/WM-Logo-Full.png';
import wmLogoCb from '../../assets/WM-Logo-CB-Transparent-White5.png';
import { useDatabaseSafe } from '../../context/DatabaseContext';
import exportReportPdf from './export/reportPdfExporter';
import exportReportExcel from './export/reportExcelExporter';
import exportReportCsv from './export/reportCsvExporter';
import buildReportFilename from './export/reportFilename';
import { generatedStamp } from '../../utils/dateTime';

import prepareReportPdf, {
  toCamelCase, fmtDate, splitHeaderLabel, sortReportRows, computeParamHeaderWidths,
  FIXED_GROUP_BY, INDEX_REPORT_HIDDEN_FIELDS, INDEX_SPEC_COL_MAP, applyIndexSpecLabels,
} from './pdf/prepareReportPdf';
import { buildReportPages } from './pdf/ReportPdfDocument';
import { BROWSER_MAX_PDF_PAGES } from './pdf/reportPdfProfiles';
import { followServerPdfJob } from './pdf/serverPdfJob';
import StuiSelect from '../../components/common/StuiSelect';

// Canvas-based text measurement for fixed header column sizing
const _mc = document.createElement('canvas').getContext('2d');
const measureTextPx = (text, font) => { _mc.font = font; return _mc.measureText(String(text)).width; };

const ReportViewerPage = () => {
  const { reportSlug, dbSlug } = useParams();
  const location = useLocation();
  const { notify } = useNotifications();
  const dataTransfer = useDataTransfer();
  const { registerActivity, updateActivity, completeActivity, failActivity } = dataTransfer;
  // The whole context changes with every activity tick; the server PDF path reads it through a
  // ref so the export handlers stay stable.
  const dataTransferRef = useRef(dataTransfer);
  dataTransferRef.current = dataTransfer;
  const dbContext = useDatabaseSafe();
  const { currentUser } = useAuth();
  const dbName = dbContext?.currentDb?.databaseName || activeDbName() || 'DB';

  const [config, setConfig] = useState(null);
  const [clientLogoSrc, setClientLogoSrc] = useState(null);
  const reportDisplayName = config?.reportName || '';
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [executionMs, setExecutionMs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [parameters, setParameters] = useState({});
  const [committedParameters, setCommittedParameters] = useState({});
  const [hasRun, setHasRun] = useState(false);
  const [lastRunParams, setLastRunParams] = useState(null);

  // Sort state — 4 slots driving multi-level sort
  const EMPTY_SLOT = { field: '', dir: 'asc' };
  const [sortSlots, setSortSlots] = useState([EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT]);

  // Pagination state — page size derived from physical PDF page dimensions
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [selectedPageSize, setSelectedPageSize] = useState(null); // null = use config default

  // Fixed pixel widths for parameter header columns — computed once from all possible values
  const [paramHeaderWidths, setParamHeaderWidths] = useState({});
  // Pre-fetched options per field — passed to comboboxes so they render at correct width immediately
  const [paramOptions, setParamOptions] = useState({});
  // Gate: don't render comboboxes until param options are fetched (prevents resize jump on load)
  const [paramOptionsReady, setParamOptionsReady] = useState(false);

  // Export grouping state
  const [exportGroupBy, setExportGroupBy] = useState(null);
  const [exportGroupSeparate, setExportGroupSeparate] = useState(false);

  // Report options
  const [showQrCode, setShowQrCode] = useState(false);

  // Custom title / watermark — free-text inputs for reports with type:'label' or type:'watermark' parameters
  const [customTitle, setCustomTitle] = useState('');
  const [watermark, setWatermark] = useState('');

  // Report subscriptions (step 103 P4). hasData is what the menu's data-dependent items gate on
  // now that the menu button itself always opens — see the comment on the button.
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  // Export action menu (6-dot header menu)
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);
  useEffect(() => {
    if (!exportMenuOpen) return;
    const handler = (e) => { if (!exportMenuRef.current?.contains(e.target)) setExportMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [exportMenuOpen]);

  // QR code data URL — generated from the current report URL + encoded parameters
  const [qrDataUrl, setQrDataUrl] = useState(null);

  // DAWN version string — fetched once, displayed in report footer.
  // Uses the AllowAnonymous /current-version endpoint (a20.046) so
  // non-SysAdmin report viewers get the version rendered on exports.
  const [dawnVersion, setDawnVersion] = useState('');
  useEffect(() => {
    devFilesApi.getCurrentVersion()
      .then(res => setDawnVersion(res.data?.currentVersion || ''))
      .catch(() => {});
  }, []);

  // Tracks which reportSlug the currently loaded config belongs to — prevents stale-config
  // param fetches when navigating report-to-report (sibling effects see pre-setState values)
  const configSlugRef = useRef(null);
  const defaultSortRef = useRef([]);
  // Holds compact {field:[id]} map from ?p= QR URL — resolved after options load
  const urlParamsRef = useRef(null);

  // Scale entire preview (header + table + footer) as one unit — zoom-independent
  const previewContainerRef = useRef(null);
  const previewContentRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  // Load report config from API
  useEffect(() => {
    if (!reportSlug) return;

    // Reset all run state when navigating to a different report
    setData([]);
    setTotalCount(0);
    setExecutionMs(null);
    setHasRun(false);
    setLastRunParams(null);
    setCurrentPage(1);
    setConfig(null);
    setExportGroupBy(null);
    setExportGroupSeparate(false);
    setShowQrCode(false);
    setCommittedParameters({});
    setCustomTitle('');
    setWatermark('');
    configSlugRef.current = null;
    urlParamsRef.current = null;
    setParamHeaderWidths({});
    setParamOptions({});
    setParamOptionsReady(false);

    setConfigLoading(true);
    reportingApi.getConfig(reportSlug)
      .then(res => {
        configSlugRef.current = reportSlug;
        const PARAM_ORDER = {
          Source: 1, Drawing: 2, Network: 3, Parent: 4,
          Panel: 5, Subnet: 5, Equipment: 6, Index: 7, Tag: 8, 'Port Type': 9
        };
        const sortedConfig = {
          ...res.data,
          parameters: [...(res.data.parameters || [])].sort((a, b) => {
            const oa = PARAM_ORDER[a.field] ?? PARAM_ORDER[a.label] ?? 99;
            const ob = PARAM_ORDER[b.field] ?? PARAM_ORDER[b.label] ?? 99;
            return oa - ob;
          })
        };
        setConfig(sortedConfig);
        // Force fixed grouping for reports that always group by a specific field
        if (FIXED_GROUP_BY[reportSlug]) setExportGroupBy(FIXED_GROUP_BY[reportSlug]);
        const savedParams = location?.state?.params || null;

        // If the URL contains ?p=<base64>, stash the compact {field:[id]} map in a ref.
        // Actual reconstruction happens after paramOptions load (we need display names).
        try {
          const pParam = new URLSearchParams(window.location.search).get('p');
          if (pParam) urlParamsRef.current = JSON.parse(atob(pParam));
        } catch (_) { /* ignore malformed */ }

        // Start with defaults (or router state); QR params override after options load
        const initialParams = {};
        (res.data.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').forEach(p => {
          initialParams[p.field] = savedParams?.[p.field]
            ?? [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
        });
        setParameters(initialParams);

        const configSort = (res.data.sorting || []).map(s => ({
          field: s.field,
          dir: (s.direction || 'asc').toLowerCase()
        }));
        defaultSortRef.current = configSort;
        const filled = [...configSort, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT].slice(0, 4);
        setSortSlots(filled);
      })
      .catch(err => {
        notify.error(`Failed to load report config: ${err.message}`, 'error');
      })
      .finally(() => setConfigLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportSlug]);

  // Fetch client logo and convert to base64 for PDF (worker needs serialisable data, not a URL).
  // Goes through /api/proxy/image to avoid CORS on the Azure blob storage origin.
  // Preview uses iconUrl directly — no fetch needed there.
  const clientIconUrl = dbContext?.currentDb?.iconUrl || null;
  const clientName = dbContext?.currentDb?.description || dbContext?.currentDb?.displayName || '';
  useEffect(() => {
    if (!clientIconUrl) { setClientLogoSrc(null); return; }
    let live = true;
    fetchImageAsDataUrl(clientIconUrl).then(dataUrl => { if (live) setClientLogoSrc(dataUrl); });
    return () => { live = false; };
  }, [clientIconUrl]);

  // Set by the re-download trigger below, consumed by the firing effect that sits after the export
  // handlers are defined. It exists because the trigger cannot call an export itself — see there.
  const [pendingAutoExport, setPendingAutoExport] = useState(null);

  // Auto-export trigger — when navigated from the activity panel's "Re-download".
  // Keyed on state.nonce rather than a one-shot boolean: navigation is now router state, so
  // re-downloading the SAME report twice in a session does not remount this component and a
  // once-only ref would silently ignore every attempt after the first.
  const autoExportNonceRef = useRef(null);
  useEffect(() => {
    const state = location?.state;
    if (!state?.autoExport || !config) return;
    const nonce = state.nonce ?? 'legacy';
    if (autoExportNonceRef.current === nonce) return;
    autoExportNonceRef.current = nonce;
    const doAutoExport = async () => {
      // Push the recipe's filters into the live pickers too, matching what the ?p= deep link does.
      // Without this the dropdowns sit at their defaults while the data below them came from the
      // recipe's filters, so the page contradicts itself and reads as stale. Still ALSO passed to
      // runReport as an override, because setParameters is async and runReport would otherwise
      // close over the previous value.
      if (state.params) setParameters(state.params);

      // Render options, restored only when the recipe actually carries them. A recipe written
      // before these fields existed simply leaves the report's own defaults in place — which is
      // the deliberate rule, not an accident of the code.
      const r = state.recipe;
      if (r) {
        if (Array.isArray(r.sortOrder)) {
          // sortOrder holds semantics (precedence order); the UI wants four fixed slots.
          const slots = r.sortOrder.slice(0, 4).map(s => ({ field: s.field, dir: s.direction || 'asc' }));
          while (slots.length < 4) slots.push(EMPTY_SLOT);
          setSortSlots(slots);
        }
        if (r.exportGroupBy !== undefined)       setExportGroupBy(r.exportGroupBy);
        if (r.exportGroupSeparate !== undefined) setExportGroupSeparate(!!r.exportGroupSeparate);
        if (r.showQrCode !== undefined)          setShowQrCode(!!r.showQrCode);
        if (r.customTitle !== undefined)         setCustomTitle(r.customTitle || '');
        if (r.watermark !== undefined)           setWatermark(r.watermark || '');
      }

      await runReport(state.params || undefined);

      // Hand off to the effect below rather than calling the export here. This function closed
      // over the render that started it, so its handleExport* references still see the pre-run
      // sortedData — an empty array, which every export handler guards against and returns on.
      // The old code slept 500ms hoping to dodge that, but a stale closure does not refresh with
      // time. Setting state instead re-runs the trigger effect with handlers bound to live data.
      setPendingAutoExport(state.autoExport);
    };
    doAutoExport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, location?.state]);

  // After config loads, fetch all param options and compute fixed column widths.
  // Guard: configSlugRef.current must match reportSlug — prevents stale-config fetches
  // when navigating report-to-report (React batches sibling effect state updates, so
  // config still holds the previous report's value when this effect fires after slug change).
  useEffect(() => {
    if (!config || !reportSlug) return;
    if (configSlugRef.current !== reportSlug) return;
    const params = (config.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark');
    if (params.length === 0) return;

    Promise.all(
      params.map(p =>
        reportingApi.getParameterOptions(reportSlug, p.field, null)
          .then(res => ({ field: p.field, label: p.label, options: res.data || [] }))
          .catch(() => ({ field: p.field, label: p.label, options: [] }))
      )
    ).then(results => {
      // Same rule the server-side renderer uses, so header widths match in every PDF.
      const widths = computeParamHeaderWidths(results);
      setParamHeaderWidths(widths);
      const optMap = {};
      results.forEach(({ field, options }) => { optMap[field] = options; });
      setParamOptions(optMap);
      setParamOptionsReady(true);

      // Resolve QR ?p= compact params now that we have display names
      if (urlParamsRef.current) {
        const compact = urlParamsRef.current;
        urlParamsRef.current = null;
        const resolved = {};
        (config.parameters || []).forEach(p => {
          const ids = compact[p.field];
          if (ids && optMap[p.field]) {
            const matched = optMap[p.field].filter(opt =>
              ids.includes(String(opt.id ?? opt.value ?? ''))
            );
            resolved[p.field] = matched.length > 0
              ? matched.map(opt => ({
                  type: 'item',
                  id: opt.id ?? opt.value,
                  display: opt.display,
                  fullDisplay: opt.fullDisplay || opt.display,
                  operator: 'eq',
                  value: opt.value ?? opt.id
                }))
              : [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          } else {
            resolved[p.field] = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
          }
        });
        setParameters(resolved);
      }
    });

    // No params — ready immediately
    if (params.length === 0) setParamOptionsReady(true);
  }, [config, reportSlug]);

  // Derive sortOrder from the 4 slot selects
  const sortOrder = useMemo(() =>
    sortSlots.filter(s => s.field).map(s => ({ field: s.field, direction: s.dir })),
  [sortSlots]);

  // Sort data client-side
  const sortedData = useMemo(() => sortReportRows(data, sortOrder), [data, sortOrder]);

  const exportGroupCount = useMemo(() => {
    if (!exportGroupBy || !sortedData.length) return 0;
    const cf = toCamelCase(exportGroupBy);
    const seen = new Set();
    sortedData.forEach(row => {
      const val = row[`${cf}_Display`] ?? row[cf];
      seen.add(val != null ? String(val) : '(Blank)');
    });
    return seen.size;
  }, [exportGroupBy, sortedData]);

  // Column widths for preview — same content-aware algorithm as PDF export so
  // the preview table layout matches the PDF exactly (percentages of 1176pt content area).
  const PDF_CONTENT_WIDTH_PT = 1176; // TABLOID landscape 1224pt minus 24pt × 2 horizontal padding
  const previewColWidths = useMemo(() => {
    if (!config || !sortedData.length) return {};
    const isIndexRpt = !!FIXED_GROUP_BY[reportSlug];
    const allCols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    // Index reports: exclude the group field and the spec-label columns from the width table
    const cols = isIndexRpt ? allCols.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field)) : allCols;
    // Measure at 8px — preview renders at 8px. Apply ptFactor so 6pt-font reports
    // compute tighter column minimums matching what the PDF actually renders.
    const previewFontSize = config.layout?.fontSize || 8;
    const ptFactor = previewFontSize <= 6 ? 0.75 : 1.0;
    const dataFont = '8px Helvetica, sans-serif';
    const headerFont = 'bold 8px Helvetica, sans-serif';
    const CELL_PAD_PT = 4;
    const WRAPPABLE_FIELDS = new Set(['Note', 'Comment', 'Note_DES']);
    const minWidths = {};
    cols.forEach(c => {
      const camelField = toCamelCase(c.field);
      const displayKey = `${camelField}_Display`;
      const isWrappable = WRAPPABLE_FIELDS.has(c.field);
      let maxDataPx = 0;
      if (!isWrappable) {
        sortedData.forEach(row => {
          const val = row[displayKey] != null ? row[displayKey] : row[camelField];
          if (val != null) maxDataPx = Math.max(maxDataPx, measureTextPx(String(fmtDate(val)), dataFont));
        });
      }
      // For Index report spec columns, header label = IDX_SPEC_N_COL value (varies per group).
      // Measure the widest label across all rows so the column fits any group's header.
      let headerPx;
      if (isIndexRpt) {
        const specEntry = INDEX_SPEC_COL_MAP.find(([, sf]) => sf === c.field);
        if (specEntry) {
          // fkSourceField columns come back as {camel}_Display — check that key first
          const camelColField = toCamelCase(specEntry[0]);
          let maxLabelPx = 0;
          sortedData.forEach(row => {
            const lbl = row[`${camelColField}_Display`] || row[camelColField];
            if (lbl) maxLabelPx = Math.max(maxLabelPx, measureTextPx(String(lbl), headerFont));
          });
          headerPx = maxLabelPx || measureTextPx(c.label, headerFont);
        }
      }
      if (headerPx == null) {
        const [hLine1, hLine2] = splitHeaderLabel(c.label);
        headerPx = Math.max(measureTextPx(hLine1, headerFont), hLine2 ? measureTextPx(hLine2, headerFont) : 0);
      }
      minWidths[c.field] = Math.max(67, Math.ceil(Math.max(maxDataPx, headerPx) * ptFactor) + CELL_PAD_PT);
    });
    // Wrappable column floors: Note ≥ Description width; Comment ≥ 333px
    if (minWidths.Note != null) {
      minWidths.Note = Math.max(minWidths.Note, minWidths.Description || minWidths.Tag_Description || 67);
    }
    if (minWidths.Comment != null) {
      minWidths.Comment = Math.max(minWidths.Comment, 233);
    }
    if (minWidths.Note_DES != null) {
      minWidths.Note_DES = Math.max(minWidths.Note_DES, 233);
    }
    const totalMinPt = cols.reduce((sum, c) => sum + minWidths[c.field], 0);
    const colWidths = {};
    if (totalMinPt >= PDF_CONTENT_WIDTH_PT) {
      cols.forEach(c => { colWidths[c.field] = `${((minWidths[c.field] / totalMinPt) * 100).toFixed(1)}%`; });
    } else {
      const remaining = PDF_CONTENT_WIDTH_PT - totalMinPt;
      const totalConfigPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
      cols.forEach(c => {
        const configShare = (c.pixelWidth || c.width * 10 || 100) / totalConfigPx;
        const extraPt = remaining * configShare;
        colWidths[c.field] = `${(((minWidths[c.field] + extraPt) / PDF_CONTENT_WIDTH_PT) * 100).toFixed(1)}%`;
      });
    }
    return colWidths;
  }, [config, sortedData, reportSlug]);

  // URL embedded in the QR code — compact {field:[id]} map, skipping __all__ defaults.
  // Stripping display names and default fields keeps the payload tiny so the QR code
  // stays at a low version (sparse pattern = easier to scan and print at low DPI).
  //
  // Built from committedParameters: the QR is printed into the PDF next to the filter header,
  // so scanning it has to reproduce the run that was printed, not whatever the dropdowns hold now.
  const reportUrl = useMemo(() => {
    if (!hasRun || !config) return null;
    const compact = {};
    Object.entries(committedParameters).forEach(([field, sels]) => {
      // Skip fields where the only selection is the "All" default
      if (sels.length === 1 && sels[0].id === '__all__') return;
      compact[field] = sels.map(s => String(s.id));
    });
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(compact))));
    return `${window.location.origin}${window.location.pathname}?p=${encoded}`;
  }, [hasRun, config, committedParameters]);

  // Regenerate QR data URL whenever the report URL changes
  useEffect(() => {
    if (!reportUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(reportUrl, {
      width: 400, margin: 2, // 400px source — prints cleanly at 300dpi up to ~1.3"
      color: { dark: '#1a5568', light: '#ffffff' }
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [reportUrl]);

  // Calculate rows per page from actual PDF layout measurements (points)
  const pageSize = useMemo(() => {
    if (!config?.layout) return 47;

    // Page dimensions in points (72pt = 1 inch), landscape orientation
    const pageDimensions = {
      letter:  { width: 792, height: 612 },   // 11x8.5
      tabloid: { width: 1224, height: 792 }    // 17x11
    };

    const dims = pageDimensions[config.layout.defaultPageSize] || pageDimensions.tabloid;

    // Fixed measurements matching ReportPdfStyles.js exactly
    const pageMarginTop = 30;
    const pageMarginBottom = 40;
    const headerHeight = 115;     // worst-case: 2 param rows × maxHeight(48) + margins/border(19)
    const colHeaderHeight = 16;   // paddingV(8) + fontSize(6.5) + border(1.5)
    const fontSize = config.layout.fontSize || 8;
    const rowHeight = fontSize + 5.5;  // fontSize + paddingV(2.5×2) + border(0.5)

    const usableHeight = dims.height - pageMarginTop - pageMarginBottom - headerHeight - colHeaderHeight;
    const rowsPerPage = Math.floor(usableHeight / rowHeight);

    const calculated = Math.max(10, rowsPerPage);
    const RPP_MAX = { 'slugRptStd-Des-Cable_Schedule': 37 };
    return Math.min(calculated, RPP_MAX[reportSlug] ?? 40);
  }, [config, reportSlug]);

  // Grouped page descriptors — built when Group By is active
  const groupedPages = useMemo(() => {
    if (!exportGroupBy || !hasRun || !sortedData.length) return null;
    const cf = toCamelCase(exportGroupBy);
    const displayKey = `${cf}_Display`;
    const groupColLabel = config?.parameters?.find(p => p.field === exportGroupBy)?.label
      || config?.columns?.find(c => c.field === exportGroupBy)?.label
      || exportGroupBy;
    const groupMap = new Map();
    sortedData.forEach(row => {
      const val = row[displayKey] != null ? row[displayKey] : row[cf];
      const key = val != null ? String(val) : '(Blank)';
      if (!groupMap.has(key)) groupMap.set(key, []);
      groupMap.get(key).push(row);
    });
    const pages = [];
    [...groupMap.entries()]
      .sort(([a], [b]) => {
        if (a === '(Blank)') return 1;
        if (b === '(Blank)') return -1;
        return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
      })
      .forEach(([groupVal, groupRows]) => {
        const subtitle = `(${groupColLabel}: ${groupVal})`;
        for (let i = 0; i < Math.max(groupRows.length, 1); i += pageSize) {
          pages.push({ subtitle, groupRowCount: groupRows.length, rows: groupRows.slice(i, i + pageSize) });
        }
      });
    return pages.length > 0 ? pages : null;
  }, [exportGroupBy, sortedData, pageSize, config, hasRun]);

  // Paginate sorted data
  const totalPages = groupedPages
    ? groupedPages.length
    : Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    if (groupedPages) return groupedPages[currentPage - 1]?.rows || [];
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [groupedPages, sortedData, currentPage, pageSize]);

  // Run report
  const runReport = useCallback(async (overrideParams) => {
    if (!config) return;

    setLoading(true);
    try {
      // Commit whatever we actually query with. When called with overrideParams (deep link,
      // re-download) those are NOT the live dropdown state, so committing `parameters` would
      // record filters the run never used.
      const runParams = overrideParams ?? parameters;
      const paramPayload = {};
      Object.entries(runParams).forEach(([field, selections]) => {
        const camelField = toCamelCase(field);
        // "All" means no filter on this field — exclude from payload entirely
        const filtered = (selections || []).filter(s => s.id !== '__all__');
        if (filtered.length > 0) {
          paramPayload[camelField] = filtered.map(s => ({
            operator: s.operator,
            value: s.value
          }));
        }
      });

      const res = await reportingApi.runReport(reportSlug, { parameters: paramPayload });
      setData(res.data.data);
      setTotalCount(res.data.totalCount);
      setExecutionMs(res.data.executionMs);
      setHasRun(true);
      setCommittedParameters({ ...runParams });
      setLastRunParams(JSON.stringify(runParams));
      setCurrentPage(1);
    } catch (err) {
      notify.error(`Report failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [config, parameters, notify, reportSlug]);

  // Recalculate preview scale whenever data/page/zoom changes.
  // Only active after a report has been run — pre-run the header is a flex layout
  // with no minimum width anchor so scale thrashes. Lock to 1 until data is loaded.
  useLayoutEffect(() => {
    if (!hasRun) {
      setPreviewScale(1);
      return;
    }

    const calcScale = () => {
      if (!previewContainerRef.current || !previewContentRef.current) return;
      // Measure natural content width directly — the width compensation formula
      // (width: 100/previewScale %) means scrollWidth already equals natural width,
      // so no need to reset to scale=1 first (that reset caused a visible flash).
      requestAnimationFrame(() => {
        if (!previewContainerRef.current || !previewContentRef.current) return;
        const containerWidth = previewContainerRef.current.clientWidth;
        const contentWidth = previewContentRef.current.scrollWidth;
        setPreviewScale(contentWidth > containerWidth ? containerWidth / contentWidth : 1);
      });
    };

    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [paginatedData, hasRun]);

  // Reset to page 1 when grouping selection changes
  useEffect(() => { setCurrentPage(1); }, [exportGroupBy]);

  const updateParameter = (field, selections) => {
    setParameters(prev => {
      const updated = { ...prev, [field]: selections };
      const ALL_SELECTION = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
      const hasSpecific = (sels) => (sels || []).some(s => s.type === 'picked' || s.type === 'freetext');
      // For every other parameter: if it has no specific selection, reset it to "All"
      (config?.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').forEach(p => {
        if (p.field === field) return;
        if (!hasSpecific(prev[p.field])) {
          updated[p.field] = ALL_SELECTION;
        }
      });
      return updated;
    });
  };

  const clearFiltersAndSort = () => {
    const ALL = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
    const reset = {};
    (config?.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').forEach(p => { reset[p.field] = ALL; });
    setParameters(reset);
    setSortSlots([EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT]);
  };

  const resetToDefaults = () => {
    const ALL = [{ type: 'special', id: '__all__', display: 'All', operator: 'all', value: '__all__' }];
    const reset = {};
    (config?.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').forEach(p => { reset[p.field] = ALL; });
    setParameters(reset);
    const filled = [...defaultSortRef.current, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT, EMPTY_SLOT].slice(0, 4);
    setSortSlots(filled);
  };

  const activeFilterCount = Object.values(parameters).reduce((sum, s) =>
    sum + (s || []).filter(x => x.id !== '__all__').length, 0);
  const isCurrent = hasRun && lastRunParams === JSON.stringify(parameters);
  // Every menu item that needs rows gates on this. Subscribe included: a subscription freezes the
  // recipe from the last run, so there has to have BEEN one.
  const hasData = hasRun && sortedData.length > 0;
  const [exportingType, setExportingType] = useState(null);

  // Section collapse state — all expanded by default
  const [sec1Collapsed, setSec1Collapsed] = useState(false);
  const [sec2Collapsed, setSec2Collapsed] = useState(false);
  const cancelExportRef = useRef(false);
  const activeWorkerRef = useRef(null); // { worker, reject } — set while PDF worker is running
  const exportTimerRef  = useRef(null); // setInterval handle for progress ticking

  const handleCancelExport = useCallback(() => {
    cancelExportRef.current = true;
    if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
    // Immediately terminate the PDF worker if it's running
    if (activeWorkerRef.current) {
      activeWorkerRef.current.worker.terminate();
      activeWorkerRef.current.reject(new DOMException('Export cancelled by user', 'AbortError'));
      activeWorkerRef.current = null;
    }
    setExportingType(null);
    notify.info(config ? `${dbName} - ${reportDisplayName} Report - PDF - Cancelled` : 'Export cancelled.', 3000);
  }, [notify, config, dbName, reportDisplayName]);

  // One builder for every export recipe, so the six call sites cannot drift apart again.
  //
  // filterParams MUST come from committedParameters, not parameters: `parameters` is the live
  // dropdown state and can hold selections the user picked but never ran, whereas the exported
  // rows always come from the last run. Recording the live state describes a file that was never
  // produced. The on-screen filter chips already read committedParameters for the same reason.
  // A recipe has to describe the whole output, not just the rows. Two consumers depend on that:
  // Re-download replays it in the browser, and a report subscription freezes one and hands it to a
  // background job. Anything omitted here silently falls back to the report's defaults, which for
  // a subscription means quietly mailing a different document than the one the user subscribed to.
  //
  // reportPath is GONE. It was stored as "/reports/{slug}" with no database slug and nothing has
  // read it since task 1184 replaced it with dbSlug + reportId; keeping a field that resolves to
  // the wrong page is worse than not having one.
  const buildExportRecipe = useCallback((format, reportName, fileName) => ({
    reportId:     reportSlug,
    reportName,
    format,
    filterParams: committedParameters,
    fileName,
    // Render options. sortOrder rather than the four sortSlots: the slots are a UI shape (fixed
    // positions), whereas sortOrder is the semantic content (fields in precedence order) and is
    // what a server-side renderer can consume directly.
    sortOrder,
    exportGroupBy,
    exportGroupSeparate,
    showQrCode,
    customTitle,
    watermark,
    // No literal fallback. The old default here was 'gwa', which is not a slug — the real one is
    // 'gwa01' — so it produced a URL that resolves to no database and redirects to
    // /select-database. dbSlug is always present under the "/:dbSlug/*" route, this tab's database
    // covers the theoretical gap, and an empty value makes consumers hide Re-download rather than
    // navigate somewhere wrong.
    dbSlug:       dbSlug || activeDbSlug(),
    dbName,
  }), [reportSlug, committedParameters, dbSlug, dbName,
       sortOrder, exportGroupBy, exportGroupSeparate, showQrCode, customTitle, watermark]);

  // A PDF too large for the browser, built by the server: queue a job with the same recipe the
  // browser would have used, then follow it in Data Transfer until the file saves. Following is
  // app-level (serverPdfJob.js), so leaving the viewer or closing the tab does not lose the file.
  // One job per person at a time; asking again while one runs follows that one.
  const runServerPdf = useCallback(async (pages, exportRecipe) => {
    const label = `${dbName} - ${reportDisplayName} Report`;
    notify.status(`${label} - PDF - ${pages.toLocaleString()} pages, building on the server`, 0);
    let job;
    try {
      job = (await reportPdfJobsApi.create({
        reportSlug, reportName: reportDisplayName, databaseSlug: exportRecipe.dbSlug, recipe: exportRecipe,
      })).data;
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.jobId) {
        notify.warning(data.error, 6000);
        try { job = (await reportPdfJobsApi.get(data.jobId)).data; } catch { /* reported below */ }
      }
      if (!job) {
        notify.clearStatus();
        notify.error(`${label} - PDF - ${data?.error || err.message || 'Could not start the server build.'}`);
        return;
      }
    }
    const outcome = await followServerPdfJob(job, { dt: dataTransferRef.current, notify, exportRecipe });
    if (outcome === 'other-tab') {
      notify.clearStatus();
      notify.info(`${job.activityName} - PDF - already being followed in another tab; it saves there.`, 6000);
    }
  }, [reportSlug, dbName, reportDisplayName, notify]);

  // Clear all filters — reset each parameter back to "All"
  // PDF export
  const handleExportPdf = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;

    // ── Timer setup ──────────────────────────────────────────────────────────
    const _start  = Date.now();

    notify.status(`${dbName} - ${reportDisplayName} Report - PDF - Started`, 0);
    // ─────────────────────────────────────────────────────────────────────────

    // Columns, widths, header, grouping: the shared prep the server-side renderer also runs.
    const prep = prepareReportPdf({
      config,
      reportSlug,
      reportName: reportDisplayName,
      rows: sortedData,
      sortOrder,
      parameters: committedParameters,
      paramHeaderWidths,
      groupBy: exportGroupBy || undefined,
      groupSeparate: exportGroupSeparate,
      customTitle,
      watermark,
      logoSrc: wmLogo,
      clientLogoSrc,
      clientName,
      qrDataUrl: showQrCode ? qrDataUrl : undefined,
      dawnVersion,
      measureText: measureTextPx,
    });

    // A file too big to build in the browser goes to the server instead, before anything starts
    // here. Exact pages per file (grouped reports add pages per group; separate files are checked
    // one by one), from the same pagination the document renders with. The server builds one file
    // per job, so separate files over the limit are still refused.
    const exportRecipe = buildExportRecipe('pdf', reportDisplayName, buildReportFilename(reportDisplayName, 'pdf'));
    const largestPdf = Math.max(...prep.documents.map(d => buildReportPages(d.props)?.length || 1));
    if (largestPdf > BROWSER_MAX_PDF_PAGES) {
      if (prep.separate) {
        notify.clearStatus();
        notify.error(
          `${reportDisplayName}: one of these PDFs would be ${largestPdf.toLocaleString()} pages, over the ${BROWSER_MAX_PDF_PAGES}-page limit ` +
          'for building in the browser, and the server builds a single file only. Turn off separate files, add filters, or export to Excel.');
        return;
      }
      runServerPdf(largestPdf, exportRecipe);
      return;
    }

    // ── Data Transfer registration ───────────────────────────────────────────
    const activityName = `${dbName} - ${reportDisplayName} Report`;
    registerActivity(`pdf-${reportSlug}`, {
      name: activityName, type: 'download', format: 'pdf',
      phase: 'Preparing…', pct: 0,
      restartFn: () => handleExportPdf(),
      exportRecipe,
      rowsTransferred: sortedData.length,
    });

    // ── Estimated duration + progress timer ─────────────────────────────────
    // At the report's real rows per page, the same estimate the size guards use.
    const pageCount = prep.pageCount;
    const _estSec = Math.max(5, pageCount * 0.8);
    let _phaseLabel = 'Building…';

    if (exportTimerRef.current) clearInterval(exportTimerRef.current);
    exportTimerRef.current = setInterval(() => {
      const elapsed = (Date.now() - _start) / 1000;
      const pct = Math.min(94, Math.round((elapsed / _estSec) * 100));
      updateActivity(`pdf-${reportSlug}`, { phase: _phaseLabel, pct });
    }, 1000);

    const onPhase = (phase, info) => {
      switch (phase) {
        case 'building':   _phaseLabel = 'Building document…'; break;
        case 'rendering':  _phaseLabel = `Rendering ${pageCount} pages…`; break;
        case 'download':   _phaseLabel = 'Writing file…'; break;
        case 'ready': {
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          // Size is shown because it is genuinely useful (a 20 MB PDF is worth knowing about
          // before you mail it) and because it is the measurement that decides whether
          // subscribed PDFs can be attachments or need blob storage — step 103 item 6.
          const mb = info?.bytes ? ` (${(info.bytes / 1048576).toFixed(1)} MB)` : '';
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished${mb}`, 5000);
          completeActivity(`pdf-${reportSlug}`, { rowsTransferred: sortedData.length });
          break;
        }
        case 'cancelled':
          // handleCancelExport has already announced the cancel.
          clearInterval(exportTimerRef.current); exportTimerRef.current = null;
          failActivity(`pdf-${reportSlug}`, { cancelled: true });
          break;
        default: break;
      }
    };

    try {
      // Phase handler that defers the success/complete signals during multi-file loops
      const onPhaseSilent = (phase) => {
        switch (phase) {
          case 'building':  _phaseLabel = 'Building document…'; break;
          case 'rendering': _phaseLabel = `Rendering pages…`; break;
          case 'download':  _phaseLabel = 'Writing file…'; break;
          case 'ready':     break; // deferred — handled after loop
          case 'cancelled':
            clearInterval(exportTimerRef.current); exportTimerRef.current = null;
            failActivity(`pdf-${reportSlug}`, { cancelled: true });
            break;
          default: break;
        }
      };

      if (prep.separate) {
        // Separate PDF file per unique group value — auto-download each
        const _pdfBatchTs = Date.now();
        let _pdfPageIdx = 0;
        for (const doc of prep.documents) {
          if (cancelExportRef.current) break;
          const result = await exportReportPdf({
            ...doc.props,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
            onPhase: onPhaseSilent,
            onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
          });
          activeWorkerRef.current = null;
          if (result === false || cancelExportRef.current) break;
          const pageId = `pdf-${reportSlug}-${_pdfBatchTs}-${_pdfPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${doc.reportName}`, type: 'download', format: 'pdf',
            exportRecipe: buildExportRecipe('pdf', doc.reportName, buildReportFilename(doc.reportName, 'pdf')),
            rowsTransferred: doc.props.data?.length,
          });
          completeActivity(pageId, { rowsTransferred: doc.props.data?.length });
        }
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        if (!cancelExportRef.current) {
          notify.success(`${dbName} - ${reportDisplayName} Report - PDF - Finished`, 5000);
          completeActivity(`pdf-${reportSlug}`, { rowsTransferred: sortedData.length });
        }
      } else {
        const result = await exportReportPdf({
          ...prep.documents[0].props,
          isCancelled: () => cancelExportRef.current,
          onWorkerReady: (worker, reject) => { activeWorkerRef.current = { worker, reject }; },
          onPhase,
          onProgress: (msg) => setExportingType(msg ? 'pdf' : null)
        });
        activeWorkerRef.current = null;
        if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
        // Exporter returns false when the user cancels the save dialog or the export. The
        // Cancel Export path has already ended the activity through onPhase('cancelled').
        if (result === false) {
          setExportingType(null);
          if (!cancelExportRef.current) failActivity(`pdf-${reportSlug}`, { cancelled: true });
        }
      }
    } catch (err) {
      if (exportTimerRef.current) { clearInterval(exportTimerRef.current); exportTimerRef.current = null; }
      console.error('PDF export failed:', err);
      setExportingType(null);
      failActivity(`pdf-${reportSlug}`);
      notify.error(`${dbName} - ${reportDisplayName} Report - PDF - Failed: ${err.message || 'unknown error'}`);
    }
  }, [config, sortedData, sortOrder, committedParameters, paramHeaderWidths, exportGroupBy, exportGroupSeparate, reportSlug, notify, dbName, reportDisplayName, clientLogoSrc, clientName, showQrCode, qrDataUrl, dawnVersion, customTitle, watermark, buildExportRecipe, runServerPdf, registerActivity, updateActivity, completeActivity, failActivity]);

  // Excel export
  const handleExportExcel = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `excel-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'excel',
      phase: 'Generating Excel…', pct: 0,
      restartFn: () => handleExportExcel(),
      exportRecipe: buildExportRecipe('excel', reportDisplayName, buildReportFilename(reportDisplayName, 'xlsx')),
      rowsTransferred: sortedData.length,
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - Excel - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        // Separate file per group — auto-download each
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        const _xlsBatchTs = Date.now();
        let _xlsPageIdx = 0;
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          const pageReportName = `${reportDisplayName} - ${groupVal}`;
          await exportReportExcel({
            config: { ...config, reportName: pageReportName },
            columns: cols, data: groupRows, parameters: committedParameters,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'excel' : null)
          });
          if (cancelExportRef.current) break;
          const pageId = `excel-${reportSlug}-${_xlsBatchTs}-${_xlsPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${pageReportName}`, type: 'download', format: 'excel',
            exportRecipe: buildExportRecipe('excel', pageReportName, buildReportFilename(pageReportName, 'xlsx')),
            rowsTransferred: groupRows.length,
          });
          completeActivity(pageId, { rowsTransferred: groupRows.length });
        }
      } else {
        await exportReportExcel({
          config: { ...config, reportName: reportDisplayName },
          columns: cols,
          data: sortedData,
          parameters: committedParameters,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'excel' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - Excel - Finished`, 5000);
      completeActivity(actId, { rowsTransferred: sortedData.length });
    } catch (err) {
      console.error('Excel export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - Excel - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, committedParameters, exportGroupBy, exportGroupSeparate, reportSlug, notify, dbName, reportDisplayName, buildExportRecipe, registerActivity, completeActivity, failActivity]);

  // CSV export
  const handleExportCsv = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    cancelExportRef.current = false;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    const actId = `csv-${reportSlug}`;
    registerActivity(actId, {
      name: `${dbName} - ${reportDisplayName} Report`, type: 'download', format: 'csv',
      phase: 'Generating CSV…', pct: 0,
      restartFn: () => handleExportCsv(),
      exportRecipe: buildExportRecipe('csv', reportDisplayName, buildReportFilename(reportDisplayName, 'csv')),
      rowsTransferred: sortedData.length,
    });
    notify.status(`${dbName} - ${reportDisplayName} Report - CSV - Started`, 0);
    try {
      if (exportGroupBy && exportGroupSeparate) {
        const groupMap = {};
        sortedData.forEach(row => {
          const cf = toCamelCase(exportGroupBy);
          const val = row[`${cf}_Display`] != null ? row[`${cf}_Display`] : row[cf];
          const key = val != null ? String(val) : '(Blank)';
          if (!groupMap[key]) groupMap[key] = [];
          groupMap[key].push(row);
        });
        const _csvBatchTs = Date.now();
        let _csvPageIdx = 0;
        for (const [groupVal, groupRows] of Object.entries(groupMap).sort(([a], [b]) => {
          if (a === '(Blank)') return 1;
          if (b === '(Blank)') return -1;
          return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
        })) {
          if (cancelExportRef.current) break;
          const pageReportName = `${reportDisplayName} - ${groupVal}`;
          await exportReportCsv({
            config: { ...config, reportName: pageReportName },
            columns: cols, data: groupRows,
            autoDownload: true,
            isCancelled: () => cancelExportRef.current,
            onProgress: (msg) => setExportingType(msg ? 'csv' : null)
          });
          if (cancelExportRef.current) break;
          const pageId = `csv-${reportSlug}-${_csvBatchTs}-${_csvPageIdx++}`;
          registerActivity(pageId, {
            name: `${dbName} - ${pageReportName}`, type: 'download', format: 'csv',
            exportRecipe: buildExportRecipe('csv', pageReportName, buildReportFilename(pageReportName, 'csv')),
            rowsTransferred: groupRows.length,
          });
          completeActivity(pageId, { rowsTransferred: groupRows.length });
        }
      } else {
        await exportReportCsv({
          config: { ...config, reportName: reportDisplayName },
          columns: cols,
          data: sortedData,
          groupByField: exportGroupBy || undefined,
          isCancelled: () => cancelExportRef.current,
          onProgress: (msg) => setExportingType(msg ? 'csv' : null)
        });
      }
      notify.success(`${dbName} - ${reportDisplayName} Report - CSV - Finished`, 5000);
      completeActivity(actId, { rowsTransferred: sortedData.length });
    } catch (err) {
      console.error('CSV export failed:', err);
      notify.info(`${dbName} - ${reportDisplayName} Report - CSV - Cancelled`, 3000);
      setExportingType(null);
      failActivity(actId);
    }
  }, [config, sortedData, exportGroupBy, exportGroupSeparate, reportSlug, notify, dbName, reportDisplayName, buildExportRecipe, registerActivity, completeActivity, failActivity]);

  // Fires a re-download's export once the report has actually produced rows.
  //
  // This deliberately lives AFTER the export handlers, and is a separate effect from the trigger
  // that queued it. The trigger's async body closes over the render that started it, so its
  // handleExport* references still see the pre-run sortedData — an empty array, which every
  // handler guards against and returns on. This effect instead re-runs once sortedData is
  // populated, so the handler it invokes is the live one. (The trigger used to sleep 500ms and
  // call the handler directly, which cannot work: waiting does not refresh a closure. That path
  // was unreachable until a35.014, so the flaw had never been exercised.)
  useEffect(() => {
    if (!pendingAutoExport || !hasRun || sortedData.length === 0) return;
    const fmt = pendingAutoExport;
    setPendingAutoExport(null);
    if (fmt === 'pdf')   handleExportPdf();
    if (fmt === 'excel') handleExportExcel();
    if (fmt === 'csv')   handleExportCsv();
  }, [pendingAutoExport, hasRun, sortedData, handleExportPdf, handleExportExcel, handleExportCsv]);

  if (configLoading || !paramOptionsReady) {
    return <div className="stui-module-loading">Loading report configuration...</div>;
  }

  if (!config) {
    return <div className="stui-module-loading">Report not found: {reportSlug}</div>;
  }

  const columns = [...(config.columns || [])].sort((a, b) => a.order - b.order);
  const isIndexReport = !!FIXED_GROUP_BY[reportSlug];
  // Preview/PDF columns with hidden fields removed (Index reports only)
  const displayColumns = isIndexReport
    ? columns.filter(c => !INDEX_REPORT_HIDDEN_FIELDS.has(c.field))
    : columns;
  // Header columns for the current preview page — spec labels replaced from the current group's first row
  const currentPageHeaderColumns = isIndexReport && paginatedData.length > 0
    ? applyIndexSpecLabels(displayColumns, paginatedData[0])
    : displayColumns;
  const canRun = !loading;
  const runLabel = loading ? 'Running...' : isCurrent ? 'Refresh' : 'Run Report';

  // Build sort lookup for column headers
  const sortLookup = {};
  sortOrder.forEach((s, i) => {
    sortLookup[s.field] = { direction: s.direction, priority: i + 1 };
  });

  return (
    <div className="stui-module-container stui-report-viewer">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <FileSpreadsheet size={20} />
          <h1>{reportDisplayName}</h1>
          {config.description && (
            <span className="stui-module-header-meta">{config.description}</span>
          )}
        </div>
        <div className="stui-module-header-actions">
        {/* 6-dot export menu */}
        <div className="stui-action-menu" ref={exportMenuRef}>
          {/* The menu used to be disabled outright until the report had run with rows, because
              everything on it was an export. "My Subscriptions" is not: you must be able to pause
              or delete a subscription without running the report first. So the BUTTON always
              opens and the items that need data disable themselves — which is how Clear Filters &
              Sort already behaved. */}
          <button
            className="stui-action-menu-btn"
            onClick={() => setExportMenuOpen(o => !o)}
          >
            <GripVertical size={16} />
          </button>
          {exportMenuOpen && (
            <div className="stui-action-menu-dropdown">
              <button
                className="stui-action-menu-item"
                onClick={() => { resetToDefaults(); setExportMenuOpen(false); }}
              >
                <FilterX size={16} /><span>Default Filters &amp; Sort</span>
              </button>
              <button
                className={`stui-action-menu-item${activeFilterCount === 0 && sortOrder.length === 0 ? ' disabled' : ''}`}
                disabled={activeFilterCount === 0 && sortOrder.length === 0}
                onClick={() => { clearFiltersAndSort(); setExportMenuOpen(false); }}
              >
                <FilterX size={16} /><span>Clear Filters &amp; Sort</span>
              </button>
              <div className="stui-action-menu-divider" />
              <label
                className="stui-action-menu-item"
                style={{ cursor: (hasData && exportGroupBy) ? 'pointer' : 'not-allowed', opacity: (hasData && exportGroupBy) ? 1 : 0.4 }}
                onClick={e => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  className="stui-checkbox-input"
                  checked={exportGroupSeparate}
                  disabled={!hasData || !exportGroupBy}
                  onChange={e => setExportGroupSeparate(e.target.checked)}
                />
                <span>Separate Files{exportGroupBy && exportGroupCount > 0 ? ` (${exportGroupCount})` : ''}</span>
              </label>
              <button className={`stui-action-menu-item${!hasData ? ' disabled' : ''}`} disabled={!hasData || !!exportingType} onClick={() => { handleExportPdf(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Download PDF</span>
              </button>
              <button className={`stui-action-menu-item${!hasData ? ' disabled' : ''}`} disabled={!hasData || !!exportingType} onClick={() => { handleExportExcel(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Export Excel</span>
              </button>
              <button className={`stui-action-menu-item${!hasData ? ' disabled' : ''}`} disabled={!hasData || !!exportingType} onClick={() => { handleExportCsv(); setExportMenuOpen(false); }}>
                <Download size={16} /><span>Export CSV</span>
              </button>
              {exportingType && (
                <>
                  <div className="stui-action-menu-divider" />
                  <button className="stui-action-menu-item stui-action-menu-item-danger" onClick={() => { handleCancelExport(); setExportMenuOpen(false); }}>
                    <Ban size={16} /><span>Cancel Export</span>
                  </button>
                </>
              )}
              <div className="stui-action-menu-divider" />
              {/* Subscribe needs a run to freeze; Manage deliberately does not — you must be able
                  to pause or delete without running the report first. */}
              <button
                className={`stui-action-menu-item${!hasData ? ' disabled' : ''}`}
                disabled={!hasData}
                title={hasData ? undefined : 'Run the report first — a subscription stores the filters you ran it with'}
                onClick={() => { setSubscribeOpen(true); setExportMenuOpen(false); }}
              >
                <Mail size={16} /><span>Subscribe&hellip;</span>
              </button>
              <button
                className="stui-action-menu-item"
                onClick={() => { setManageOpen(true); setExportMenuOpen(false); }}
              >
                <Mail size={16} /><span>My Subscriptions&hellip;</span>
              </button>
            </div>
          )}
        </div>
        </div>{/* stui-module-header-actions */}
      </div>{/* stui-module-header */}

      <div className="stui-tab-body">

      {/* ===== Section 1: Report Configuration ===== */}
      {config.parameters && config.parameters.length > 0 && (
        <div className="stui-step-panel">
          <div
            className="stui-step-header clickable"
            onClick={() => setSec1Collapsed(c => !c)}
          >
            <span className="stui-step-number">1</span>
            <h3>Report Configuration</h3>
            <span className="stui-step-chevron">
              {sec1Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </span>
          </div>
          {!sec1Collapsed && <p className="stui-step-desc">Select filter values to narrow report results.</p>}
          {!sec1Collapsed && <div>
            {(config.parameters || []).filter(p => p.type === 'label' || p.type === 'watermark').map(p => (
              <div key={p.field} className="stui-row-8" style={{ padding: 'var(--spacing-sm) var(--spacing-sm) var(--spacing-xs)' }}>
                <label className="stui-text-xs stui-fw-semibold stui-text-nowrap" style={{ color: 'var(--color-gray-600)' }}>
                  {p.label}:
                </label>
                <input
                  type="text"
                  value={p.type === 'label' ? customTitle : watermark}
                  onChange={e => p.type === 'label' ? setCustomTitle(e.target.value) : setWatermark(e.target.value)}
                  placeholder={`Enter ${p.label}…`}
                  className="stui-input stui-flex-1"
                  style={{ maxWidth: '400px' }}
                />
              </div>
            ))}
            <ReportParameterTable
              params={(config.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark')}
              values={parameters}
              onChange={(field, sels) => updateParameter(field, sels)}
              reportId={reportSlug}
              paramOptions={paramOptions}
              sortLookup={Object.fromEntries(sortOrder.map((s, i) => [s.field, i + 1]))}
            />
            {/* Options row: Sort | Group By | QR */}
            <div className="stui-options-row">
              {/* Sort By comboboxes */}
              {[
                { label: 'Sort by', idx: 0 },
                { label: 'Then',    idx: 1 },
                { label: 'Then',    idx: 2 },
                { label: 'Then',    idx: 3 },
              ].map(({ label, idx }) => (
                <div key={idx} className="stui-sort-slot">
                  <span className="stui-label">{label}:</span>
                  <StuiSelect
                    className="stui-select"
                    value={sortSlots[idx].field}
                    onChange={e => {
                      const updated = sortSlots.map((s, i) => i === idx ? { ...s, field: e.target.value } : s);
                      setSortSlots(updated);
                    }}
                  >
                    <option value="">—</option>
                    {(config?.columns || []).map(col => (
                      <option key={col.field} value={col.field}>{col.label || col.field}</option>
                    ))}
                  </StuiSelect>
                  {sortSlots[idx].field && (
                    <StuiSelect
                      className="stui-select"
                      value={sortSlots[idx].dir}
                      onChange={e => {
                        const updated = sortSlots.map((s, i) => i === idx ? { ...s, dir: e.target.value } : s);
                        setSortSlots(updated);
                      }}
                    >
                      <option value="asc">Asc</option>
                      <option value="desc">Desc</option>
                    </StuiSelect>
                  )}
                </div>
              ))}
              {/* Group By */}
              {((config?.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').length > 0) && (
                <div className="stui-sort-slot">
                  <span className="stui-label">Group By:</span>
                  <StuiSelect
                      className="stui-select"
                      value={exportGroupBy || ''}
                      onChange={e => setExportGroupBy(e.target.value || null)}
                      disabled={!!FIXED_GROUP_BY[reportSlug]}
                      title={FIXED_GROUP_BY[reportSlug] ? "Grouping is fixed for this report and cannot be changed" : "Group the report preview and export output by unique values of this column"}
                    >
                      <option value="">—</option>
                      {(config?.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark').map(col => <option key={col.field} value={col.field}>{col.label}</option>)}
                    </StuiSelect>
                </div>
              )}
              {/* QR Code checkbox */}
              <label className="stui-checkbox">
                <input type="checkbox" className="stui-checkbox-input" checked={showQrCode} onChange={e => setShowQrCode(e.target.checked)} />
                Show QR Code in report header
              </label>
            </div>
            {/* Stats row */}
            <div className="stui-stat-row">
              <div className="stui-stat-group">
                {[
                  { label: 'Rows',      value: hasRun ? totalCount.toLocaleString() : '—' },
                  { label: 'Pages',     value: hasRun ? totalPages : '—' },
                  { label: 'Rows/Page', value: hasRun ? pageSize : '—' },
                  { label: 'Layout',    value: config?.layout?.defaultPageSize || 'tabloid' },
                  { label: 'Time',      value: hasRun && executionMs != null ? `${executionMs}ms` : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="stui-stat-item">
                    <span className="stui-stat-label">{label}</span>
                    <span className="stui-stat-value">{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>}
        </div>
      )}

      {/* ===== Section 2: Report Preview ===== */}
      <div className="stui-step-panel stui-step-panel-no-overflow">
        <div
          className="stui-step-header clickable"
          onClick={() => setSec2Collapsed(c => !c)}
        >
          <span className="stui-step-number">2</span>
          <h3>Report Preview</h3>
          <button
            className="stui-btn stui-btn-primary stui-step-action"
            onClick={e => { e.stopPropagation(); runReport(); }}
            disabled={!canRun}
            title={activeFilterCount === 0 ? 'Select at least one parameter before running' : undefined}
          >
            <Play size={16} /> {runLabel}
          </button>
          <span className="stui-step-chevron">
            {sec2Collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>

      {!sec2Collapsed && <>
      <p className="stui-step-desc">Preview matches the PDF export layout.</p>
      {/* Pre-run placeholder — no scale wrapper, no reflow risk */}
      {!hasRun && !loading && (
        <div className="stui-report-placeholder">
          <p>Set parameters and click <strong>Run Report</strong> to view data.</p>
        </div>
      )}

      {/* Scaled preview — only rendered after first run */}
      {hasRun && (
      <>
      <div ref={previewContainerRef} className="stui-rpt-preview-container">
      <div
        ref={previewContentRef}
        className="stui-rpt-preview-scale"
        style={{
          transform: `scale(${previewScale})`,
          width: '1632px', // TABLOID landscape: 1224pt × (96/72) px/pt
          marginBottom: previewScale < 1 ? `${(previewScale - 1) * (previewContentRef.current?.offsetHeight ?? 0)}px` : 0,
          position: 'relative'
        }}
      >
      {watermark && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          pointerEvents: 'none', overflow: 'hidden', zIndex: 10
        }}>
          <div style={{
            width: '700px', transform: 'rotate(10deg)',
            textAlign: 'center', fontSize: '65px', fontWeight: 900,
            color: 'rgba(0,0,0,0.13)', textTransform: 'uppercase',
            letterSpacing: '0.08em', userSelect: 'none',
            wordBreak: 'break-word', lineHeight: 1.1
          }}>
            {watermark}
          </div>
        </div>
      )}

      {/* Report Page Header — mirrors PDF output exactly */}
      <div className="stui-rpt-preview-header">
        {/* Left: WM logo + QR code */}
        <div className="stui-rpt-preview-logo-block">
          <img src={wmLogo} alt="Wunderlich-Malec" style={{ height: '96px', width: 'auto' }} />
          {showQrCode && qrDataUrl && (
            <div className="stui-rpt-preview-qr-block">
              <img
                src={qrDataUrl}
                alt="QR Code"
                title="Scan to open this report in DAWN"
                style={{ height: '86px', width: '86px', imageRendering: 'pixelated' }}
              />
              <span className="stui-rpt-preview-qr-label">Scan to open in DAWN</span>
            </div>
          )}
        </div>

        {/* Spacer to push parameters right */}
        <div className="stui-rpt-preview-spacer" />

        {/* Center: Report Title — absolutely centered over full header width */}
        <div className="stui-rpt-preview-title-block">
          {customTitle && (
            <div style={{ fontSize: '10px', color: config?.branding?.titleColor || '#1f497d', textAlign: 'center', marginBottom: '2px', letterSpacing: '0.5px' }}>
              {customTitle}
            </div>
          )}
          <div className="stui-rpt-preview-title" style={{ color: config?.branding?.titleColor || '#1f497d' }}>
            {reportDisplayName}
          </div>
          {groupedPages?.[currentPage - 1]?.subtitle && (
            <div className="stui-rpt-preview-subtitle" style={{ color: config?.branding?.titleColor || '#1f497d' }}>
              {groupedPages[currentPage - 1].subtitle}
            </div>
          )}
          {hasRun && (
            <div className="stui-rpt-preview-record-count">
              {groupedPages
                ? `${(groupedPages[currentPage - 1]?.groupRowCount || 0).toLocaleString()} Records`
                : `${sortedData.length.toLocaleString()} Records`}
            </div>
          )}
        </div>

        {/* Right: Selected parameters — fixed 3-col × 2-row grid (6 positions).
            Params anchor to position 1 (bottom-right) and fill toward position 6 (top-left).
            Values frozen at last run time (committedParameters), not live selections. */}
        {(() => {
          const allParams = (config.parameters || []).filter(p => p.type !== 'label' && p.type !== 'watermark');
          const params = isIndexReport
            ? allParams.filter(p => p.field !== 'Index_Sheet')
            : allParams;

          // Build 6-slot array: slots[0]=pos6 .. slots[5]=pos1
          // With n params: last n slots are filled, earlier slots are null (empty)
          const NUM_SLOTS = 6;
          const slots = Array(NUM_SLOTS).fill(null);
          params.forEach((p, i) => { slots[NUM_SLOTS - params.length + i] = p; });

          // Column c: top=slots[c*2], bottom=slots[c*2+1]
          const colWidths = [0, 1, 2].map(c =>
            Math.max(
              slots[c * 2] ? (paramHeaderWidths[slots[c * 2].field] || 80) : 0,
              slots[c * 2 + 1] ? (paramHeaderWidths[slots[c * 2 + 1].field] || 80) : 0,
              80
            )
          );

          const renderSlot = (slotIdx, colWidth) => {
            const param = slots[slotIdx];
            if (!param) return <div key={`empty-${slotIdx}`} className="stui-rpt-preview-param-slot" style={{ width: `${colWidth}px` }} />;
            const selsList = committedParameters[param.field] || [];
            return (
              <div key={param.field} className="stui-rpt-preview-param-slot" style={{ width: `${colWidth}px` }}>
                <div className="stui-rpt-preview-param-label">{param.label}</div>
                {selsList.length === 0 ? (
                  <div className="stui-rpt-preview-param-none">None Selected</div>
                ) : (
                  <>
                    {(selsList.length > 4 ? selsList.slice(0, 3) : selsList).map((sel, i) => (
                      <div key={i} title={sel.fullDisplay || sel.display} className="stui-rpt-preview-param-value">
                        {sel.fullDisplay || sel.display}
                      </div>
                    ))}
                    {selsList.length > 4 && (
                      <div className="stui-rpt-preview-param-overflow">Greater than 4 Selected</div>
                    )}
                  </>
                )}
              </div>
            );
          };

          return (
            <div className="stui-rpt-preview-params">
              <div className="stui-rpt-preview-params-row">
                {[0, 2, 4].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
              <div className="stui-rpt-preview-params-row">
                {[1, 3, 5].map((si, ci) => renderSlot(si, colWidths[ci]))}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Data Table */}
      {hasRun && (
        <div className="stui-rpt-preview-table-wrap">
          <table className="stui-rpt-preview-table">
            <thead className="stui-rpt-preview-thead">
              <tr>
                {currentPageHeaderColumns.map(col => {
                  const sortInfo = sortLookup[col.field];
                  return (
                    <th
                      key={col.field}
                      title={col.label}
                      className="stui-rpt-preview-th"
                      style={{
                        width: previewColWidths[col.field] || `${col.pixelWidth || col.width * 10 || 80}px`,
                        textAlign: col.align || 'left',
                      }}
                    >
                      {(() => {
                        const [hLine1, hLine2] = splitHeaderLabel(col.label);
                        return (
                          <>
                            <span className="stui-rpt-preview-th-line">{hLine1}</span>
                            <span className="stui-rpt-preview-th-line">{hLine2}</span>
                            <span className="stui-rpt-preview-th-sort">
                              {sortInfo
                                ? `${sortInfo.priority} ${sortInfo.direction === 'asc' ? 'asc' : 'dsc'}`
                                : '\u00a0'}
                            </span>
                          </>
                        );
                      })()}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length} className="stui-rpt-preview-empty-row">
                    No data returned
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    className={`stui-rpt-preview-row${config.layout?.zebraStriping && idx % 2 === 1 ? ' stui-rpt-preview-row-alt' : ''}`}
                  >
                    {displayColumns.map(col => {
                      const camelField = toCamelCase(col.field);
                      const displayKey = `${camelField}_Display`;
                      const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

                      const isCellWrappable = col.field === 'Note' || col.field === 'Comment' || col.field === 'Note_DES';
                      return (
                        <td
                          key={col.field}
                          className={`stui-rpt-preview-td${isCellWrappable ? ' wrap' : ''}`}
                          style={{ textAlign: isCellWrappable ? 'left' : (col.align || 'left') }}
                        >
                          {displayValue != null ? String(fmtDate(displayValue)) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Report Preview Footer — mirrors PDF footer, inside previewContentRef so width matches table */}
      {hasRun && (
        <div className="stui-rpt-preview-footer">
          <span>{generatedStamp()}</span>
          {(clientIconUrl || clientName) && (
            <span className="stui-rpt-preview-footer-org">
              {clientIconUrl && <img src={clientIconUrl} alt="Client" style={{ height: '16px', width: 'auto', objectFit: 'contain' }} />}
              {clientName} Confidential
            </span>
          )}
          {dawnVersion && <span>Dawn - {dawnVersion}</span>}
          <span className="stui-rpt-preview-footer-wm">
            <img src={wmLogoCb} alt="Wunderlich-Malec" style={{ height: '16px', width: 'auto' }} />
            Wunderlich-Malec Confidential
          </span>
          <span>{hasRun ? `Page ${currentPage} of ${totalPages}` : ''}</span>
        </div>
      )}

      </div>{/* close previewContentRef */}
      </div>{/* close previewContainerRef */}

      {/* Paginator — outside scaled area so controls stay full size */}
      {hasRun && (
        <ReportPaginator
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={sortedData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}

      </>
      )}{/* close hasRun */}
      </>}{/* close !sec2Collapsed */}

      </div>{/* close Section 2 stui-step-panel */}

      </div>{/* stui-tab-body */}

      {/* Subscribe passes the SAME recipe the exporters build, so "the report I subscribed to"
          and "the file I just downloaded" are the same description — including the render options
          and the committed (not live) filters. */}
      {subscribeOpen && (
        <ReportSubscriptionModal
          mode="subscribe"
          reportSlug={reportSlug}
          reportName={reportDisplayName}
          dbSlug={dbSlug || activeDbSlug()}
          recipe={buildExportRecipe('excel', reportDisplayName, buildReportFilename(reportDisplayName, 'xlsx'))}
          currentUserId={currentUser?.id}
          onClose={() => setSubscribeOpen(false)}
        />
      )}
      {manageOpen && (
        <ReportSubscriptionModal
          mode="manage"
          reportSlug={reportSlug}
          reportName={reportDisplayName}
          dbSlug={dbSlug || activeDbSlug()}
          currentUserId={currentUser?.id}
          onClose={() => setManageOpen(false)}
        />
      )}
    </div>
  );
};

export default ReportViewerPage;
